import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useLazyQuery } from '@apollo/client';

import { useAuth } from '@seller-area/wrappers/auth-wrapper';

import { createClientsReportApolloClient } from './apollo-client';
import { buildClientsReportGraphqlVariables } from './build-report-params';
import { EMPTY_NULL_KPIS, ClientsNullKpiKey } from './null-kpi-config';
import { CLARKE_CLIENTS_REPORT_QUERY } from './queries';
import { ClientReportRow, ClientsReportContextType, ClientsReportFilters, ClientsReportIndicators } from './types';

const DEFAULT_INDICATORS: ClientsReportIndicators = {
  totalCompanies: 0,
  totalVolumeMwm: 0,
  totalUnits: 0,
  nullKpis: EMPTY_NULL_KPIS,
};

const DEFAULT_FILTERS: ClientsReportFilters = {
  term: '',
  nameFilter: '',
  legalNameFilter: '',
  accountManagerIdFilter: null,
  docType: null,
  docNumberFilter: '',
  companyIdFilter: '',
  clientIdFilter: '',
  status: 'CUSTOMER',
  type: null,
  exposure: null,
  volumeMwmRange: null,
  exposurePercentageRange: null,
  unitsCountRange: null,
  clientSinceRange: null,
  churnDateRange: null,
  createdAtRange: null,
  updatedAtRange: null,
};

const GATEWAY_SCHEMA_HINT =
  'Se estiver usando o Gateway, publique o schema (rover) após o deploy do backend. Em local, use VITE_CUSTOMER_API_GRAPHQL apontando para a customer API.';

export const ClientsReportContext = createContext<ClientsReportContextType>({
  clients: [],
  indicators: DEFAULT_INDICATORS,
  total: 0,
  loading: false,
  queryError: null,
  page: 0,
  limit: 50,
  filters: DEFAULT_FILTERS,
  sortBy: null,
  sortOrder: 'asc',
  activeNullKpi: null,
  setPage: () => undefined,
  setLimit: () => undefined,
  setSort: () => undefined,
  setFilter: () => undefined,
  clearFilter: () => undefined,
  clearAllFilters: () => undefined,
  toggleNullKpi: () => undefined,
  fetchClients: () => undefined,
});

export const ClientsReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading: authLoading, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const reportClient = useMemo(() => createClientsReportApolloClient(tokenRef), []);

  const [page, setPageState] = useState(0);
  const [limit, setLimitState] = useState(50);
  const [filters, setFiltersState] = useState<ClientsReportFilters>(DEFAULT_FILTERS);
  const [clients, setClients] = useState<ClientReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [indicators, setIndicators] = useState<ClientsReportIndicators>(DEFAULT_INDICATORS);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [sortBy, setSortByState] = useState<string | null>(null);
  const [sortOrder, setSortOrderState] = useState<'asc' | 'desc'>('asc');
  const [activeNullKpi, setActiveNullKpi] = useState<ClientsNullKpiKey | null>(null);

  const [runQuery, { loading }] = useLazyQuery(CLARKE_CLIENTS_REPORT_QUERY, {
    client: reportClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const report = data?.clarkeClientsReport;
      if (report) {
        setQueryError(null);
        setClients(report.data ?? []);
        setTotal(report.total ?? 0);
        setIndicators(report.indicators ?? DEFAULT_INDICATORS);
      }
    },
    onError: (error) => {
      setClients([]);
      setTotal(0);
      setIndicators(DEFAULT_INDICATORS);
      const message = error.message?.includes('clarkeClientsReport')
        ? `Query clarkeClientsReport indisponível no GraphQL configurado. ${GATEWAY_SCHEMA_HINT}`
        : error.message || 'Não foi possível carregar o relatório de empresas.';
      setQueryError(message);
    },
  });

  const runQueryRef = useRef(runQuery);
  runQueryRef.current = runQuery;

  useEffect(() => {
    if (authLoading || !token) return;

    runQueryRef.current({
      variables: buildClientsReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [authLoading, token, page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  const fetchClients = useCallback(() => {
    runQueryRef.current({
      variables: buildClientsReportGraphqlVariables(page, limit, filters, sortBy, sortOrder, activeNullKpi),
    });
  }, [page, limit, filters, sortBy, sortOrder, activeNullKpi]);

  const setSort = useCallback((col: string, order: 'asc' | 'desc') => {
    setSortByState(col);
    setSortOrderState(order);
    setPageState(0);
  }, []);

  const setPage = useCallback((p: number) => setPageState(p), []);
  const setLimit = useCallback((l: number) => {
    setLimitState(l);
    setPageState(0);
  }, []);

  const setFilter = useCallback((key: keyof ClientsReportFilters, value: unknown) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
    setPageState(0);
  }, []);

  const clearFilter = useCallback((key: keyof ClientsReportFilters) => {
    setFiltersState((prev) => ({ ...prev, [key]: DEFAULT_FILTERS[key] }));
    setPageState(0);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setSortByState(null);
    setSortOrderState('asc');
    setActiveNullKpi(null);
    setPageState(0);
  }, []);

  const toggleNullKpi = useCallback((key: ClientsNullKpiKey) => {
    setActiveNullKpi((prev) => (prev === key ? null : key));
    setPageState(0);
  }, []);

  const value = useMemo(
    () => ({
      clients,
      indicators,
      total,
      loading: loading || authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchClients,
    }),
    [
      clients,
      indicators,
      total,
      loading,
      authLoading,
      queryError,
      page,
      limit,
      filters,
      activeNullKpi,
      sortBy,
      sortOrder,
      setPage,
      setLimit,
      setSort,
      setFilter,
      clearFilter,
      clearAllFilters,
      toggleNullKpi,
      fetchClients,
    ],
  );

  return <ClientsReportContext.Provider value={value}>{children}</ClientsReportContext.Provider>;
};

export const useClientsReport = () => useContextSelector(ClientsReportContext, (c) => c);
